import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { Control, type ControlOptions } from "./Control.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import { alignAxis, rectContains, rectIntersects } from "../../common/geometry.js";

/**
 * An ordered collection of child controls. Index 0 paints first (bottom of the
 * z-order); the last entry paints last and is hit-tested first.
 */
export class ControlCollection implements Iterable<Control> {
  #items: Control[] = [];
  #owner: Container;

  constructor(owner: Container) {
    this.#owner = owner;
  }

  get length(): number {
    return this.#items.length;
  }

  [Symbol.iterator](): Iterator<Control> {
    return this.#items[Symbol.iterator]();
  }

  at(index: number): Control | undefined {
    return this.#items[index];
  }

  indexOf(control: Control): number {
    return this.#items.indexOf(control);
  }

  toArray(): Control[] {
    return [...this.#items];
  }

  add<T extends Control>(control: T): T {
    return this.insert(this.#items.length, control);
  }

  addRange(controls: readonly Control[]): void {
    for (const c of controls) this.add(c);
  }

  insert<T extends Control>(index: number, control: T): T {
    if ((control as Control) === (this.#owner as Control)) {
      throw new Error("webwin: a container cannot contain itself.");
    }
    if (control.contains(this.#owner)) {
      throw new Error("webwin: cannot add an ancestor as a child (would create a cycle).");
    }
    control.parent?.controls.remove(control);
    control.parent = this.#owner;
    this.#items.splice(Math.max(0, Math.min(index, this.#items.length)), 0, control);
    // Now that it has an ancestry, it may have gained a Desktop (and with it a
    // theme and the ability to measure text).
    if (this.#owner.desktop) control.notifyEnvironmentChanged();
    this.#owner.onChildrenChanged();
    // A docked child has no meaningful bounds of its own until a parent has
    // handed it an edge, so place it now rather than at the next resize.
    this.#owner.layoutDocked();
    this.#owner.invalidate();
    return control;
  }

  remove(control: Control): boolean {
    const i = this.#items.indexOf(control);
    if (i < 0) return false;
    // Before detaching, while the desktop can still be found: focus left on a removed
    // control would take every key down a tree that no longer reaches the desktop.
    this.#owner.desktop?.focusManager.clearWithin(control);
    this.#items.splice(i, 1);
    control.parent = null;
    this.#owner.onChildrenChanged();
    this.#owner.layoutDocked();
    this.#owner.invalidate();
    return true;
  }

  clear(): void {
    const focus = this.#owner.desktop?.focusManager;
    for (const c of this.#items) {
      focus?.clearWithin(c);
      c.parent = null;
    }
    this.#items.length = 0;
    this.#owner.onChildrenChanged();
    this.#owner.invalidate();
  }

  /**
   * Move an existing child to `index`, counted in the list with the control
   * already removed. Reordering only — the control is never detached, so it
   * does not re-run the attach lifecycle the way remove+insert would.
   */
  moveTo(control: Control, index: number): void {
    const i = this.#items.indexOf(control);
    if (i < 0) return;
    this.#items.splice(i, 1);
    const target = Math.max(0, Math.min(index, this.#items.length));
    if (target !== i) this.#owner.invalidate();
    this.#items.splice(target, 0, control);
  }

  /** Move a child to the top of the z-order (painted last, hit first). */
  bringToFront(control: Control): void {
    this.moveTo(control, this.#items.length);
  }

  sendToBack(control: Control): void {
    this.moveTo(control, 0);
  }

  /** Depth-first walk in paint order. */
  *descendants(): Generator<Control> {
    for (const c of this.#items) {
      yield c;
      if (c instanceof Container) yield* c.controls.descendants();
    }
  }
}

export interface ContainerOptions extends ControlOptions {
  /** Children added on construction. */
  children?: readonly Control[];
  /** Start out covered by a busy spinner. */
  busy?: boolean;
  /** Caption under the busy spinner. */
  busyText?: string;
}

/**
 * A control that hosts children.
 *
 * Children live in the container's *child space*, which may be offset (a
 * window's client area starts below its title bar), scrolled, and scaled (a
 * zooming ScrollViewer). `childToLocal` / `localToChild` are the single place
 * that mapping is defined, and painting, hit-testing and coordinate helpers
 * all route through it.
 */
export abstract class Container extends Control {
  readonly controls: ControlCollection = new ControlCollection(this);

  /** Scroll offset applied to children, in child-space units. */
  scrollX = 0;
  scrollY = 0;
  /** Uniform zoom applied to children. */
  childScale = 1;

  #busy = false;
  #busyText: string;
  /** Seconds for one turn of the busy spinner. */
  busySpinSeconds = 1.1;

  constructor(options: ContainerOptions = {}) {
    super(options);
    this.#busyText = options.busyText ?? "";
    if (options.children) this.controls.addRange(options.children);
    if (options.busy) this.busy = true;
  }

  // --- busy state -------------------------------------------------------

  /**
   * Cover this container with a spinner and hold input off its children.
   *
   * Every container has it, so a window, a panel, a tab page or one side of a
   * split all show the same thing without the application building an overlay
   * by hand. Children stay visible and enabled underneath — the point is to
   * say "wait", not to tear down what the user was looking at — but the
   * pointer and Tab cannot reach them while it is set.
   */
  get busy(): boolean {
    return this.#busy;
  }
  set busy(v: boolean) {
    if (this.#busy === v) return;
    this.#busy = v;
    this.blocksInput = v;
    if (v) this.desktop?.focusManager.clearWithin(this);
    this.#syncBusyAnimation();
    this.invalidate();
  }

  /** Optional caption drawn under the spinner. */
  get busyText(): string {
    return this.#busyText;
  }
  set busyText(v: string) {
    if (this.#busyText === v) return;
    this.#busyText = v;
    if (this.#busy) this.invalidate();
  }

  #animating = false;

  /**
   * Registered as an animator only while busy and on screen.
   *
   * Called from the setter and again from every paint, rather than from
   * `onEnvironmentChanged`: too many container subclasses override that
   * without calling super for it to be a reliable hook, and a container that
   * is busy is repainting anyway.
   */
  #syncBusyAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#busy && this.effectivelyVisible;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }


  /**
   * Region of this control's local space that children are drawn into and
   * clipped to. Defaults to the whole control; Window narrows it to the area
   * inside its frame.
   */
  get clientRect(): Rect {
    return this.localRect;
  }

  /** Size available to children, in child-space units. */
  get clientSize(): { width: number; height: number } {
    const r = this.clientRect;
    return { width: r.width / this.childScale, height: r.height / this.childScale };
  }

  childToLocal(p: Point): Point {
    const r = this.clientRect;
    return {
      x: r.x + (p.x - this.scrollX) * this.childScale,
      y: r.y + (p.y - this.scrollY) * this.childScale,
    };
  }

  localToChild(p: Point): Point {
    const r = this.clientRect;
    return {
      x: (p.x - r.x) / this.childScale + this.scrollX,
      y: (p.y - r.y) / this.childScale + this.scrollY,
    };
  }

  /** Called after the child list changes; layout containers re-arrange here. */
  onChildrenChanged(): void {}

  /**
   * Framework-internal: a child's preferred size changed and it is being measured.
   *
   * Relays out the whole container rather than the one child, because a taller bar moves
   * everything docked after it and a wider label moves every column beside it.
   */
  onChildMeasureChanged(_child: Control): void {
    this.layout();
  }

  /**
   * Children first: a container's own hook often measures its children, and a
   * theme change can alter their sizes.
   */
  override notifyEnvironmentChanged(): void {
    for (const c of this.controls) c.notifyEnvironmentChanged();
    // A theme swap changes the client area just as a resize does — different
    // title bar height, different border. Run the anchors against it so a
    // child pinned to opposite edges keeps filling the space instead of being
    // stranded at its old size. applyAnchors re-baselines as it goes.
    this.applyAnchors();
    this.applyAlignment();
    this.performDock();
    this.performLayout();
    super.notifyEnvironmentChanged();
  }

  /** Find a descendant by name. */
  findControl(name: string): Control | null {
    for (const c of this.controls.descendants()) if (c.name === name) return c;
    return null;
  }

  // --- layout -----------------------------------------------------------

  /** Client size the current anchor offsets were computed against. */
  #anchorBaseline: { width: number; height: number } | null = null;
  /** Client size captured immediately before an in-flight setBounds. */
  #clientBeforeResize: { width: number; height: number } | null = null;

  override setBounds(x: number, y: number, width: number, height: number): void {
    // clientRect depends on the outer size, so snapshot it before it changes;
    // onResize fires from inside super.setBounds and needs the old value.
    this.#clientBeforeResize = this.clientSize;
    try {
      super.setBounds(x, y, width, height);
    } finally {
      this.#clientBeforeResize = null;
    }
  }

  protected override onResize(): void {
    this.applyAnchors();
    this.applyAlignment();
    this.performDock();
    this.performLayout();
  }

  /** Re-run anchor resolution, alignment, docking, and any subclass layout. */
  layout(): void {
    this.applyAnchors();
    this.applyAlignment();
    this.performDock();
    this.performLayout();
  }

  /**
   * Re-run docking alone, after a child's `dock`, `margin` or `visible`
   * changed. The anchors are deliberately not re-run: nothing about the
   * client area moved, and re-baselining them here would swallow a resize
   * that had not been resolved yet.
   */
  layoutDocked(): void {
    if (!this.#hasDockedChild()) return;
    this.performDock();
    this.performLayout();
    this.invalidate();
  }

  /**
   * Treat the current client size as the reference anchors are measured from.
   * Called after a theme swap, which can change the client area without any
   * child being expected to move.
   */
  resetAnchorBaseline(): void {
    this.#anchorBaseline = this.clientSize;
  }

  /** Subclass hook for containers that position children themselves. */
  /**
   * Clip children to the client area. Containers that scroll must leave this
   * on; a plain panel can turn it off to let decoration spill past the edge.
   */
  clipChildren = true;

  /**
   * How far past the client area children may paint. Defaults to whatever
   * their decoration needs; a scrolling container overrides it to zero,
   * because content must not escape the viewport.
   */
  protected get clipSlack(): number {
    return this.childOverflow();
  }

  /** The furthest any visible child paints outside its own bounds. */
  protected childOverflow(): number {
    let max = 0;
    for (const c of this.controls) {
      if (c.visible) max = Math.max(max, c.paintOverflow);
    }
    return max;
  }

  protected performLayout(): void {}

  /**
   * WinForms anchoring: a child keeps its distance to each anchored edge.
   * Anchoring both left and right (or top and bottom) stretches the child.
   */
  protected applyAnchors(): void {
    const client = this.clientSize;
    const prev = this.#clientBeforeResize ?? this.#anchorBaseline;
    this.#anchorBaseline = { ...client };
    if (!prev) return;
    const dw = client.width - prev.width;
    const dh = client.height - prev.height;
    if (dw === 0 && dh === 0) return;

    for (const c of this.controls) {
      // Docked children are placed from scratch by performDock; running the
      // anchor deltas over them too would fight it.
      if (c.dock !== "none") continue;
      const a = c.anchor;
      let { x, y, width, height } = c.bounds;
      if (a.left && a.right) width += dw;
      else if (a.right) x += dw;
      else if (!a.left) x += dw / 2; // anchored to neither edge: stay centered

      if (a.top && a.bottom) height += dh;
      else if (a.bottom) y += dh;
      else if (!a.top) y += dh / 2;

      c.setBounds(x, y, Math.max(0, width), Math.max(0, height));
    }
  }

  /**
   * Place any child that asked for an alignment instead of a coordinate.
   *
   * Runs after `applyAnchors` and before `performDock`, and resolves absolutely rather than by
   * delta: an aligned child's position is a function of the client size and its margin, so
   * nothing accumulates and nothing is lost when the parent is squeezed flat and grown back.
   * Anchors are a no-op on an aligned axis for the same reason - the next line overwrites what
   * they computed.
   *
   * Docked children are skipped: docking already owns the whole placement, and the two would
   * fight over the same rectangle.
   */
  protected applyAlignment(): void {
    const client = this.clientSize;

    for (const c of this.controls) {
      if (c.dock !== "none") continue;
      const h = c.hAlign;
      const v = c.vAlign;
      const autoW = c.autoWidth;
      const autoH = c.autoHeight;
      if (h === null && v === null && !autoW && !autoH) continue;

      const m = c.margin;
      let { x, y, width, height } = c.bounds;

      // A measured child has no size of its own to align, so the extent comes from the measure.
      // `stretch` still wins: it is the parent saying how big the child is, which is the answer
      // a preferred size is only a proposal for.
      let pref: Size | null = null;
      if ((autoW && h !== "stretch") || (autoH && v !== "stretch")) {
        pref = c.preferredSize({
          width: Math.max(0, client.width - m.left - m.right),
          height: Math.max(0, client.height - m.top - m.bottom),
        });
      }
      if (pref) {
        if (autoW && h !== "stretch") width = pref.width;
        if (autoH && v !== "stretch") height = pref.height;
      }

      if (h !== null) {
        const a = alignAxis(h, client.width, width, m.left, m.right);
        x = a.offset;
        width = a.extent;
      }
      if (v !== null) {
        const a = alignAxis(v, client.height, height, m.top, m.bottom);
        y = a.offset;
        height = a.extent;
      }

      c.setBounds(x, y, width, height);
    }
  }

  /**
   * The size the children need, laid out the way `performDock` would lay them out.
   *
   * A measure of the content rather than of the container: bars docked to an edge add their
   * thickness along that axis and ask for their extent across it, whatever fills the middle asks
   * for its own preferred size, and anything placed by its own coordinates is counted where it
   * sits. The result is in child-space units, so a caller that wants an outer size adds the frame
   * itself — `localRect` minus `clientRect` — which is how one method serves a padded panel, a
   * group box with a header and a window with a title bar alike.
   *
   * Unlike a layout pass this writes nothing, so it is safe to call while deciding a size.
   */
  protected measureContent(available: Size): Size {
    let edgesW = 0;
    let edgesH = 0;
    let acrossW = 0;
    let acrossH = 0;
    let freeW = 0;
    let freeH = 0;
    let fillW = 0;
    let fillH = 0;

    for (const c of this.controls) {
      if (!c.visible) continue;
      const m = c.margin;
      const mw = m.left + m.right;
      const mh = m.top + m.bottom;
      if (c.dock === "none") {
        freeW = Math.max(freeW, c.x + c.width);
        freeH = Math.max(freeH, c.y + c.height);
        continue;
      }
      const room = {
        width: Math.max(0, available.width - edgesW - mw),
        height: Math.max(0, available.height - edgesH - mh),
      };
      const pref = c.preferredSize(room);
      switch (c.dock) {
        case "top":
        case "bottom":
          edgesH += pref.height + mh;
          acrossW = Math.max(acrossW, pref.width + mw);
          break;
        case "left":
        case "right":
          edgesW += pref.width + mw;
          acrossH = Math.max(acrossH, pref.height + mh);
          break;
        case "fill":
          fillW = Math.max(fillW, pref.width + mw);
          fillH = Math.max(fillH, pref.height + mh);
          break;
      }
    }

    return {
      width: Math.max(freeW, acrossW, edgesW + fillW),
      height: Math.max(freeH, acrossH, edgesH + fillH),
    };
  }

  #hasDockedChild(): boolean {
    for (const c of this.controls) if (c.dock !== "none") return true;
    return false;
  }

  /**
   * WinForms-style docking: each docked child takes a whole edge of what is
   * left of the client area, and `fill` takes whatever remains in the middle.
   *
   * Children are docked in the order they were added, so the first control
   * added to an edge ends up outermost — a menu bar added before a toolbar
   * sits above it. (WinForms walks its collection backwards, which surprises
   * everyone; adding top-down and reading top-down is worth the divergence.)
   *
   * Unlike `applyAnchors` this resolves absolutely: every rectangle is
   * recomputed from the current client size, nothing accumulates, and no
   * information is lost when a child is squeezed to nothing. Shrink a window
   * until its fill child is zero pixels tall and grow it back, and the child
   * is exactly the size it would have been had the window never moved — which
   * is the whole reason to prefer docking for a menu-content-status stack.
   *
   * A docked bar keeps its own size along the axis it is docked to: a status
   * bar 24px tall stays 24px tall and the space it is given only ever changes
   * across that axis. That size is never written back, so a bar docked into a
   * container too small to hold it overflows rather than being flattened and
   * forgotten.
   */
  #docking = false;

  /** True while performDock is writing child bounds. */
  get layingOutDocked(): boolean {
    return this.#docking;
  }

  protected performDock(): void {
    if (this.#docking || !this.#hasDockedChild()) return;
    this.#docking = true;
    try {
      this.#dockChildren();
    } finally {
      this.#docking = false;
    }
  }

  #dockChildren(): void {
    const size = this.clientSize;
    let left = 0;
    let top = 0;
    let right = size.width;
    let bottom = size.height;
    let fills: Control[] | null = null;

    for (const c of this.controls) {
      if (!c.visible || c.dock === "none") continue;
      const m = c.margin;
      // The middle is only known once every edge has been carved, so hold
      // them back rather than handing out space that a later bar still wants.
      if (c.dock === "fill") {
        (fills ??= []).push(c);
        continue;
      }
      const innerWidth = Math.max(0, right - left - m.left - m.right);
      const innerHeight = Math.max(0, bottom - top - m.top - m.bottom);
      // A bar that measures itself keeps no thickness of its own to read back, so ask it.
      const vertical = c.dock === "top" || c.dock === "bottom";
      const auto = vertical ? c.autoHeight : c.autoWidth;
      const pref = auto ? c.preferredSize({ width: innerWidth, height: innerHeight }) : null;
      const thick = pref ? (vertical ? pref.height : pref.width) : vertical ? c.height : c.width;
      switch (c.dock) {
        case "top":
          c.setBounds(left + m.left, top + m.top, innerWidth, thick);
          top = Math.min(bottom, top + thick + m.top + m.bottom);
          break;
        case "bottom":
          c.setBounds(left + m.left, bottom - m.bottom - thick, innerWidth, thick);
          bottom = Math.max(top, bottom - thick - m.top - m.bottom);
          break;
        case "left":
          c.setBounds(left + m.left, top + m.top, thick, innerHeight);
          left = Math.min(right, left + thick + m.left + m.right);
          break;
        case "right":
          c.setBounds(right - m.right - thick, top + m.top, thick, innerHeight);
          right = Math.max(left, right - thick - m.left - m.right);
          break;
      }
    }

    if (!fills) return;
    // More than one fill is a layout mistake rather than an error, and the
    // useful reading is that they are alternates: they get the same rectangle
    // and the last one painted wins.
    for (const c of fills) {
      const m = c.margin;
      c.setBounds(
        left + m.left,
        top + m.top,
        Math.max(0, right - left - m.left - m.right),
        Math.max(0, bottom - top - m.top - m.bottom),
      );
    }
  }

  // --- hit testing ------------------------------------------------------

  override hitTest(p: Point): Control | null {
    if (!this.visible) return null;
    if (!rectContains(this.localRect, p)) return null;
    if (!this.insideShape(p)) return null;
    // A busy container swallows the pointer rather than letting it through to
    // the parent, or the user could click the thing behind the scrim.
    if (this.#busy) return this;

    if (rectContains(this.clientRect, p)) {
      const items = this.controls;
      for (let i = items.length - 1; i >= 0; i--) {
        const child = items.at(i)!;
        if (!child.visible) continue;
        // The child maps the point rather than this loop subtracting its
        // origin, so a control drawn somewhere other than its own bounds — a
        // minimized window, parked as a stub — is hit where it is drawn.
        const hit = child.hitTest(child.fromParentSpace(p));
        if (hit) return hit;
      }
    }
    return this.hitTestSelf(p) ? this : null;
  }

  override get inputBlockRect(): Rect | null {
    return this.#busy ? this.busyRect : null;
  }

  override cursorAt(p: Point): string {
    if (this.#busy && rectContains(this.busyRect, p)) return "progress";
    return super.cursorAt(p);
  }

  // --- painting ---------------------------------------------------------

  override paint(rc: RenderContext, theme: Theme): void {
    if (!this.isPaintable(rc)) return;
    this.#syncBusyAnimation();
    this.withFlare(rc, theme, () => {
      this.onPaint(rc, theme);
      this.paintChildren(rc, theme);
      this.onPaintOverlay(rc, theme);
      if (this.#busy) this.paintBusy(rc, theme);
    });
  }

  /**
   * What the busy scrim covers. The whole control by default; `Window`
   * narrows it to the client area so its caption stays legible.
   */
  protected get busyRect(): Rect {
    return this.localRect;
  }

  /** The busy scrim, spinner and caption, over everything else this drew. */
  protected paintBusy(rc: RenderContext, theme: Theme): void {
    const r = this.busyRect;
    rc.clipped(r, () => {
      theme.drawBusyOverlay(rc, r);

      const size = Math.min(theme.metrics.spinnerSize, r.width, r.height);
      if (size <= 0) return;
      const font = theme.metrics.font;
      const caption = this.#busyText;
      const gap = 10;
      const lineHeight = caption ? Math.ceil(rc.measureText(caption, font).height) : 0;
      const block = size + (caption ? gap + lineHeight : 0);

      const top = r.y + Math.round((r.height - block) / 2);
      const cx = r.x + r.width / 2;
      theme.drawSpinner(
        rc,
        { x: Math.round(cx - size / 2), y: top, width: size, height: size },
        { phase: this.busyPhase, disabled: false },
      );
      if (!caption) return;
      const line = { x: r.x + 8, y: top + size + gap, width: Math.max(0, r.width - 16), height: lineHeight };
      rc.drawTextCentered(caption, line, font, theme.palette.text);
    });
  }

  /** Phase of the busy spinner around one turn, 0..1. */
  protected get busyPhase(): number {
    if (this.busySpinSeconds <= 0) return 0;
    const now = this.desktop?.frameTime ?? 0;
    return (now % this.busySpinSeconds) / this.busySpinSeconds;
  }

  protected paintChildren(rc: RenderContext, theme: Theme): void {
    if (this.controls.length === 0) return;
    const r = this.clientRect;

    const paint = (): void => {
      rc.pushTransform(
        r.x - this.scrollX * this.childScale,
        r.y - this.scrollY * this.childScale,
        this.childScale,
      );
      for (const child of this.controls) {
        if (!child.visible) continue;
        rc.pushTransform(child.x, child.y, 1);
        paintChild(rc, theme, child);
        rc.popTransform();
      }
      rc.popTransform();
    };

    if (!this.clipChildren) {
      // No clip of our own: children are bounded only by whatever ancestor
      // clip is already in force.
      paint();
      return;
    }

    // Widen the clip by the furthest any child paints past its own bounds, so
    // a glow or shadow on a control flush against the edge is not sliced off.
    const slack = this.clipSlack;
    rc.pushClip(
      slack > 0
        ? { x: r.x - slack, y: r.y - slack, width: r.width + slack * 2, height: r.height + slack * 2 }
        : r,
    );
    if (!rc.isClippedOut) paint();
    rc.popClip();
  }

  /** Drawn after children, outside the client clip (scrollbars, frames). */
  protected onPaintOverlay(_rc: RenderContext, _theme: Theme): void {}
}

/**
 * Paint one child at the current origin, inside whatever its motion comes to.
 *
 * For a container that places its children's paint itself - a marquee drawing its strip twice -
 * so a child with an entrance still gets it there.
 */
export function paintChild(rc: RenderContext, theme: Theme, child: Control): void {
  if (child.hasMotion) paintMoving(rc, theme, child);
  else child.paint(rc, theme);
}

/**
 * Paint a child that has motion, inside the transform and fade its groups come to.
 *
 * `reveal` waits for the child's *resting* place to be on screen, so it is asked before the
 * motion moves anything: a card that slides in from far off to the left is revealed when the
 * spot it is sliding to scrolls into view, not when its starting point does. The scale is about
 * the child's center, which is what "grow" and "pulse" mean to anybody looking at one.
 */
function paintMoving(rc: RenderContext, theme: Theme, child: Control): void {
  const onScreen = rectIntersects(rc.rectToScreen(child.localRect), rc.clip);
  const m = child.motionFrame(onScreen);
  if (!m) {
    child.paint(rc, theme);
    return;
  }
  if (m.alpha <= 0) return;
  const s = m.scale > 0 ? m.scale : 0;
  if (s === 0) return;
  const moved = m.x !== 0 || m.y !== 0 || s !== 1;
  if (moved) {
    rc.pushTransform(m.x + (child.width * (1 - s)) / 2, m.y + (child.height * (1 - s)) / 2, s);
  }
  const faded = m.alpha < 1;
  if (faded) rc.pushOpacity(m.alpha);
  child.paint(rc, theme);
  if (faded) rc.popOpacity();
  if (moved) rc.popTransform();
}
